import React, { useRef, useState, useEffect } from "react";
import { cn, sanitizeFileName } from "@/lib/utils";
import { Upload, X, ThumbsUp, Heart, Smile } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";

interface ReferralFeedbackViewProps {
  className?: string;
  illustrationUrl?: string;
  isEditMode?: boolean;
  onIllustrationChange?: (url: string | null) => void;
  rating?: number;
  onRating?: (rating: number) => void;
}

export function ReferralFeedbackView({
  className,
  illustrationUrl,
  isEditMode = false,
  onIllustrationChange,
  rating,
  onRating,
}: ReferralFeedbackViewProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [localImage, setLocalImage] = useState<string | null>(illustrationUrl || null);
  const [imageScale, setImageScale] = useState(100);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    setLocalImage(illustrationUrl || null);
  }, [illustrationUrl]);

  const handleFileUpload = async (file: File) => {
    setIsUploading(true);
    try {
      const safeName = sanitizeFileName(file.name);
      const filePath = `referral-feedback-illustrations/${Date.now()}-${safeName}`;

      const { error: uploadError } = await supabase.storage
        .from("beratung-documents")
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from("beratung-documents")
        .getPublicUrl(filePath);

      const publicUrl = urlData?.publicUrl;
      if (!publicUrl) throw new Error("Konnte keine öffentliche URL erzeugen");

      setLocalImage(publicUrl);
      onIllustrationChange?.(publicUrl);
    } catch (err) {
      console.error("Image upload failed:", err);
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveImage = () => {
    setLocalImage(null);
    onIllustrationChange?.(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Floating emoji icons
  const emojis = [
    { Icon: Smile, color: "text-yellow-400", left: "5%", top: "20%" },
    { Icon: ThumbsUp, color: "text-blue-400", left: "15%", top: "40%" },
    { Icon: Heart, color: "text-pink-400", left: "10%", top: "60%" },
    { Icon: Smile, color: "text-yellow-400", left: "25%", top: "30%" },
    { Icon: ThumbsUp, color: "text-blue-400", left: "20%", top: "50%" },
    { Icon: Heart, color: "text-pink-400", left: "30%", top: "45%" },
    { Icon: Smile, color: "text-yellow-400", left: "35%", top: "25%" },
    { Icon: ThumbsUp, color: "text-blue-400", left: "40%", top: "55%" },
  ];

  return (
    <div className={cn("p-6 space-y-6", className)}>
      {/* Title section */}
      <div className="space-y-1">
        <h2 className="text-2xl md:text-3xl font-bold">
          <span className="text-foreground">Wie hat Ihnen </span>
          <span className="text-primary">das Gespräch gefallen?</span>
        </h2>
        <p className="text-lg italic text-muted-foreground">
          Wir freuen uns auf Ihre Weiterempfehlung.
        </p>
      </div>

      {/* Main content */}
      <div className="grid grid-cols-12 gap-6">
        {/* Left: Floating emojis */}
        <div className="col-span-12 lg:col-span-5 relative min-h-[300px]">
          {emojis.map((emoji, index) => {
            const Icon = emoji.Icon;
            return (
              <div
                key={index}
                className={cn(
                  "absolute animate-bounce",
                  emoji.color
                )}
                style={{
                  left: emoji.left,
                  top: emoji.top,
                  animationDelay: `${index * 0.2}s`,
                  animationDuration: `${2 + index * 0.3}s`,
                }}
              >
                <Icon className="h-8 w-8" />
              </div>
            );
          })}
        </div>

        {/* Right: Image */}
        <div className="col-span-12 lg:col-span-7">
          <div className="border-2 border-primary rounded-lg p-4 h-full flex flex-col items-center justify-center min-h-[350px]">
            {localImage ? (
              <div className="relative w-full h-full flex flex-col items-center justify-center gap-2">
                <img
                  src={localImage}
                  alt="Feedback Illustration"
                  className="object-contain transition-transform"
                  style={{
                    maxWidth: `${imageScale}%`,
                    maxHeight: `${Math.min(320, 320 * imageScale / 100)}px`
                  }}
                />
                {isEditMode && (
                  <>
                    <Button
                      variant="destructive"
                      size="icon"
                      className="absolute top-0 right-0 h-8 w-8"
                      onClick={handleRemoveImage}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                    <div className="w-full max-w-[180px] mt-2">
                      <Label className="text-xs text-muted-foreground mb-1 block">
                        Größe: {imageScale}%
                      </Label>
                      <Slider
                        value={[imageScale]}
                        onValueChange={([v]) => setImageScale(v)}
                        min={50}
                        max={300}
                        step={10}
                        className="w-full"
                      />
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center text-center p-4 space-y-4 w-full h-full">
                {isEditMode ? (
                  <>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleFileUpload(file);
                      }}
                      className="hidden"
                    />
                    <Button
                      variant="outline"
                      disabled={isUploading}
                      className="border-dashed border-2 h-32 w-full flex flex-col gap-2"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload className="h-8 w-8 text-muted-foreground" />
                      <span className="text-muted-foreground">
                        {isUploading ? "Upload läuft..." : "Grafik hochladen"}
                      </span>
                    </Button>
                  </>
                ) : (
                  <div className="flex flex-col items-center gap-2 text-muted-foreground">
                    <Upload className="h-8 w-8" />
                    <span className="text-sm">Bild wird hier angezeigt</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
